import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Upload, Users, TrendingUp, Heart } from "lucide-react";
import Logo from "@/components/Logo";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POSITIONS = ["Store Manager", "Sales Associate", "Cashier", "Warehouse & Logistics", "Marketing", "Customer Service", "Beauty Advisor", "General Application"];
const PERKS = [
  { icon: Users, title: "Family run warmth", body: "A team of 250+ that feels like one." },
  { icon: TrendingUp, title: "Room to grow", body: "We promote from within as we expand across Kerala." },
  { icon: Heart, title: "Rooted locally", body: "Work close to home, serve your own community." },
];

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [position, setPosition] = useState("General Application");
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setResume(null);
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { toast.error("Please upload a PDF, DOC or DOCX file."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB."); return; }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Please fill in name, email and phone."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("position", position);
      fd.append("message", form.message);
      if (resume) fd.append("resume", resume);
      await axios.post(`${API}/careers`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application received, thank you! We'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setPosition("General Application");
      setResume(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-0 border-b border-[#F7F2E9]/25 rounded-none py-3 font-body text-[#F7F2E9] placeholder:text-[#F7F2E9]/40 focus:outline-none focus:border-konattu-red transition-colors";

  return (
    <main className="bg-konattu-cream" data-testid="careers-page">
      <div className="noise-overlay" />
      <div className="fixed top-0 inset-x-0 z-50 bg-konattu-cream/80 backdrop-blur-2xl border-b border-konattu-charcoal/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
          <Link to="/" data-testid="careers-logo"><Logo /></Link>
          <Link to="/" data-testid="careers-back" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-konattu-charcoal/70 hover:text-konattu-red transition-colors">
            <ArrowLeft size={14} /> BACK TO SITE
          </Link>
        </div>
      </div>

      <section className="pt-[70px]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono text-xs tracking-[0.35em] text-konattu-red">/ CAREERS AT KONATTU</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="mt-6 font-display font-extrabold text-konattu-charcoal tracking-tightest leading-[0.9] text-5xl md:text-7xl lg:text-8xl">
            Grow with<br />a Kerala legacy.
          </motion.h1>
          <p className="mt-8 max-w-2xl font-body text-konattu-charcoal/70 text-lg leading-relaxed">
            We&apos;ve grown from one store in 1998 to a group of 250+ people. As we keep expanding across Kerala, we&apos;re always looking for dependable, warm people to grow with us.
          </p>

          <div className="mt-16 grid md:grid-cols-3 gap-4">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="border border-konattu-charcoal/12 p-8 h-full bg-konattu-cream" data-testid={`perk-${i}`}>
                  <p.icon size={28} strokeWidth={1.25} className="text-konattu-red" />
                  <h3 className="mt-5 font-display font-semibold text-xl text-konattu-charcoal">{p.title}</h3>
                  <p className="mt-2 font-body text-konattu-charcoal/70 text-[15px]">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-konattu-charcoal py-24 md:py-32" data-testid="careers-form-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <span className="font-mono text-xs tracking-[0.35em] text-konattu-yellow">/ APPLY NOW</span>
            <h2 className="mt-6 font-display font-bold text-[#F7F2E9] tracking-tight leading-[0.92] text-4xl md:text-6xl">Tell us<br />about you.</h2>
            <p className="mt-8 font-body text-[#F7F2E9]/70 text-lg max-w-md">Attach your CV and we&apos;ll reach out when there&apos;s a fit. No CV handy? Send your details and a note, that works too.</p>
            <div className="mt-10">
              <div className="font-mono text-[11px] tracking-[0.3em] text-konattu-red mb-2">EMAIL HR</div>
              <a href="mailto:hr@konattugroup.com" data-testid="careers-email-hr" className="font-body text-[#F7F2E9] text-lg hover:text-konattu-red transition-colors">hr@konattugroup.com</a>
            </div>
          </div>

          <form onSubmit={submit} data-testid="careers-form" className="space-y-8">
            <input data-testid="career-name" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
            <div className="grid sm:grid-cols-2 gap-8">
              <input data-testid="career-email" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={set("email")} />
              <input data-testid="career-phone" className={inputCls} placeholder="Phone number" value={form.phone} onChange={set("phone")} />
            </div>

            <div>
              <div className="font-mono text-[11px] tracking-[0.3em] text-[#F7F2E9]/50 mb-4">POSITION</div>
              <div className="flex flex-wrap gap-2.5">
                {POSITIONS.map((p) => (
                  <button key={p} type="button" onClick={() => setPosition(p)} data-testid={`position-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-4 py-2 text-sm font-body border transition-colors ${position === p ? "bg-konattu-red border-konattu-red text-white" : "border-[#F7F2E9]/25 text-[#F7F2E9]/70 hover:border-konattu-red"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <textarea data-testid="career-message" rows={3} className={`${inputCls} resize-none`} placeholder="A short note (optional)" value={form.message} onChange={set("message")} />

            <label className="flex items-center gap-4 border border-dashed border-[#F7F2E9]/30 px-5 py-5 cursor-pointer hover:border-konattu-red transition-colors" data-testid="career-resume-label">
              <Upload size={20} className="text-konattu-yellow" />
              <span className="font-body text-sm text-[#F7F2E9]/80">{resume ? resume.name : "Attach CV / Resume (PDF, DOC, DOCX · max 5MB)"}</span>
              <input type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" data-testid="career-resume-input" />
            </label>

            <button type="submit" disabled={loading} data-testid="career-submit" className="group inline-flex items-center gap-3 bg-konattu-red text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-konattu-orange transition-colors disabled:opacity-60">
              {loading ? "Submitting..." : "Submit application"}
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </main>
  );
}
